import { faWallet } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DatePicker,
  Divider,
  Empty,
  Flex,
  Form,
  Select,
  Space,
  Typography,
  Input,
} from 'antd';

import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import { CustomInputNumber } from '@app/components';
import { Avatar } from '@app/components/Avatar';
import { RULES_IS_REQUIRE } from '@app/constants';
import type { NClients } from '@app/types';
import { divideAmount } from '@app/utils';

import type { FormInstance } from 'antd/lib';
import type { Dayjs } from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

export type RefundFormValues = {
  sum: number;
  paymentAccountId: number;
  refundDate: Dayjs;
  comment: string;
};

type PaymentAccountOption = {
  value: number;
  label: string;
  balance: number;
};

const CASHBOX_SELECT_CLASS_NAMES = {
  popup: {
    root: 'bc-refund-from-balance-window__cashbox-dropdown',
  },
};

type TRefundWindowContent = {
  client: NClients.ClientForView | null | undefined;
  form: FormInstance<RefundFormValues>;
  userName: string;
  avatarSrc: string;
  paymentAccounts: ModelPaymentAccount[];
};

export const RefundWindowContent = ({
  client,
  form,
  userName,
  avatarSrc,
  paymentAccounts,
}: TRefundWindowContent) => {
  if (!client) {
    return <Empty />;
  }

  const { NameFirst, NameLast, Balance } = client;

  const firstName = NameFirst || '';
  const lastName = NameLast || '';
  const fullName =
    firstName || lastName ? [lastName, firstName].join(' ') : 'Без имени';
  const paymentAccountOptions: PaymentAccountOption[] = paymentAccounts.map(
    ({ Id, Name, Balance: paymentAccountBalance }) => ({
      value: Id,
      label: Name?.trim() || 'Без названия',
      balance: paymentAccountBalance || 0,
    })
  );

  return (
    <>
      <Divider className="window-title-divider" />

      <Flex className="meta" gap={28}>
        <Space className="client-name">
          <Avatar src={avatarSrc} nameFirst={userName} size={40} />
          <Text>{fullName}</Text>
        </Space>

        <Space className="balance">
          <FontAwesomeIcon icon={faWallet} />
          <Text>{divideAmount(Balance)} – на балансе</Text>
        </Space>
      </Flex>

      <Divider className="meta-divider" />

      <Form form={form} className="form" layout="vertical">
        <Text className="label-require">Сумма возврата</Text>
        <Form.Item
          name="sum"
          className="sum"
          rules={[
            ...RULES_IS_REQUIRE,
            {
              validator: (_, value) => {
                if (value && Number(value) === 0) {
                  return Promise.reject('Сумма возврата должна быть больше 0');
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <CustomInputNumber
            className="required-field"
            isAllowed={(floatValue: number) =>
              floatValue >= 0.01 && floatValue <= Balance
            }
            onSuccess={({ value }) => {
              form.setFieldValue('sum', value);
            }}
          />
        </Form.Item>

        <Text className="label-require">Касса</Text>
        <Form.Item
          name="paymentAccountId"
          className="box-office"
          rules={[...RULES_IS_REQUIRE]}
        >
          <Select<number, PaymentAccountOption>
            className="refund-cashbox-select required-field"
            placeholder="Пожалуйста, выберите кассу"
            options={paymentAccountOptions}
            optionLabelProp="label"
            classNames={CASHBOX_SELECT_CLASS_NAMES}
            virtual={false}
            optionRender={({ data: { label, balance } }) => (
              <div className="refund-cashbox-option">
                <span className="refund-cashbox-option__name">{label}</span>
                <span className="refund-cashbox-option__balance">
                  Баланс: {divideAmount(balance)}
                </span>
              </div>
            )}
          />
        </Form.Item>

        <Text className="label-require">Дата возврата</Text>
        <Form.Item
          name="refundDate"
          className="refund-date"
          initialValue={null}
          rules={[...RULES_IS_REQUIRE]}
        >
          <DatePicker
            className="required-field"
            placeholder="Выберите дату возврата"
            format="DD.MM.YYYY"
            style={{ display: 'block' }}
          />
        </Form.Item>

        <Text className="label-require">Комментарий</Text>
        <Form.Item
          name="comment"
          className="comment"
          rules={[...RULES_IS_REQUIRE]}
        >
          <TextArea
            placeholder="Например, возвращено по просьбе клиента"
            rows={3}
            maxLength={400}
          />
        </Form.Item>
      </Form>
    </>
  );
};
